import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, DEFAULT_AVATAR, type Provider } from "../api/client";
import { titleize } from "../lib/format";

/** Historical team list: location group + 3 LED columns (providers.html). */
export function TeamPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [nameQ, setNameQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiGet<Array<{ providers: Provider[] }>>("/providers")
      .then((d) => {
        if (!cancelled) setProviders(d?.[0]?.providers ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const term = nameQ.trim().toLowerCase();
    if (!term) return providers;
    return providers.filter((p) =>
      (p.name || "").toLowerCase().includes(term),
    );
  }, [providers, nameQ]);

  return (
    <>
      <header className="ne__page-header">
        <div className="ne__page-header__header">
          <h2 className="ne__page-header__title">Team</h2>
        </div>
      </header>
      <div className="ne__team__view">
        <form
          className="ne__table__filters"
          onSubmit={(e) => e.preventDefault()}
        >
          <fieldset className="ne__search ne__search--expandable">
            <label>
              <input
                type="text"
                placeholder="Name"
                value={nameQ}
                onChange={(e) => setNameQ(e.target.value)}
                aria-label="Search team by name"
              />
              <i className="ne__icon__search" />
            </label>
          </fieldset>
        </form>

        {loading ? (
          <div className="ne__empty-message">
            <p>Loading team…</p>
          </div>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <div className="ne__empty-message">
            <h2>Sorry, no matching providers!</h2>
          </div>
        ) : null}

        {!loading && filtered.length > 0 ? (
          <div className="ne__team__table">
            <div className="ne__table__padding">
              <table className="ne__table ne__table--avatar ne__team__location">
                <thead className="ne__team__location-header">
                  <tr className="ne__team__location-name">
                    <td colSpan={8}>
                      <h3 className="ne__team__location-heading">
                        Cobble Hill
                      </h3>
                    </td>
                  </tr>
                  <tr className="ne__team__location-eyebrows">
                    <th />
                    <th className="ne__table__eyebrow">Name</th>
                    <th className="ne__table__eyebrow">Email</th>
                    <th className="ne__table__eyebrow">Cell phone</th>
                    <th className="ne__table__eyebrow">User type</th>
                    <th className="ne__table__eyebrow ne__table__center">
                      Seeing patients
                    </th>
                    <th className="ne__table__eyebrow ne__table__center">
                      Is a provider
                    </th>
                    <th className="ne__table__eyebrow ne__table__center">
                      Account active
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => {
                    const requestable = user.requestable === true;
                    const patientable = user.patientable === true;
                    const activated = user.account_activated === true;
                    return (
                      <tr key={user.id}>
                        <td>
                          <Link to={`/app/team/${user.id}/sched`}>
                            <figure className="ne__avatar">
                              <img
                                src={user.profile_url || DEFAULT_AVATAR}
                                alt=""
                              />
                            </figure>
                          </Link>
                        </td>
                        <td className="ne__team__name">
                          <Link to={`/app/team/${user.id}/sched`}>
                            {titleize(user.name)}
                          </Link>
                        </td>
                        <td className="ne__team__email">{user.email}</td>
                        <td className="ne__team__phone">
                          {user.bio?.phone_number}
                        </td>
                        <td className="ne__team__type">
                          {user.friendly_type || user.meta_type}
                        </td>
                        <td className="ne__table__center">
                          <div className="ne__table__led">
                            <i
                              className={`ne__icon__dot ${requestable ? "green" : "gray"}`}
                            />
                            <span>
                              {requestable
                                ? "Seeing patients"
                                : "Not seeing patients"}
                            </span>
                          </div>
                        </td>
                        <td className="ne__table__center">
                          <div className="ne__table__led">
                            <i
                              className={`ne__icon__dot ${patientable ? "green" : "gray"}`}
                            />
                            <span>
                              {patientable
                                ? "Is a provider"
                                : "Is not a provider"}
                            </span>
                          </div>
                        </td>
                        <td className="ne__table__center">
                          <div className="ne__table__led">
                            <i
                              className={`ne__icon__dot ${activated ? "green" : "gray"}`}
                            />
                            <span>
                              {activated
                                ? "Account active"
                                : "Account not active"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
